import {
  hazardClassPairProblem,
  hazardClassProblem,
  subsidiaryHazardClassProblem,
} from "encamp-shared/src/waste/hazardClassDiamond";
import {
  CharacterizationBasis,
  FlashPointQualifier,
  Issue,
  IssueLevel,
  PackingGroup,
  PhysicalState,
  WasteProfileConstituentInput,
  WasteProfileInput,
  WasteStreamDotProfileInput,
} from "generated-graphql/graphql";
import { toFlexibleNumber } from "util/flexibleNumber";

import { WasteStreamProfileData } from "./types";

export interface ConstituentFormRow {
  /** Null once someone clears the input — `FormTextField` writes null, not "". */
  name: string | null;
  casNumber: string;
  minPercent: number | null;
  maxPercent: number | null;
}

/**
 * Mirrors the mutation input, the way every other form in the app does.
 *
 * The numeric fields are typed as numbers but hold whatever the text input
 * produced, which is how the Tier II chemical forms work too: the mutation takes
 * them as `FlexibleFloat`, which turns "5.5" into a number and deliberately keeps
 * "12abc" as the string the user typed, so the compliance engine can refuse it
 * rather than `parseFloat` silently storing 12.
 */
export interface WasteProfileFormValues {
  physicalState: PhysicalState | null;
  phLow: number | null;
  phHigh: number | null;
  flashPointF: number | null;
  flashPointMinF: number | null;
  flashPointMaxF: number | null;
  flashPointQualifier: FlashPointQualifier | null;
  specificGravity: number | null;
  physicalDescription: string;
  isIgnitable: boolean;
  isCorrosive: boolean;
  isReactive: boolean;
  isToxic: boolean;
  generatingProcess: string;
  basis: CharacterizationBasis | null;
  /** Edited as the comma-separated text a sheet prints; split on submit. */
  federalWasteCodes: string;
  stateWasteCodes: string;
  profileNumber: string;
  tsdfName: string;
  tsdfProfileId: string;
  constituents: ConstituentFormRow[];
  dotProperShippingName: string;
  dotIdNumber: string;
  dotHazardClass: string;
  dotSubsidiaryHazardClass: string;
  dotPackingGroup: PackingGroup | null;
  dotHazardous: boolean;
}

const toNullableString = (value: string): string | null => value?.trim() || null;

/**
 * Split an edited code list into the array the profile stores, uppercased and
 * de-duplicated: "d001, D001" is one code typed twice, and the regulation writes
 * them in capitals.
 */
const splitCodeList = (value: string): string[] => {
  const seen = new Set<string>();
  for (const code of value.split(",")) {
    const trimmed = code.trim().toUpperCase();
    if (trimmed !== "") seen.add(trimmed);
  }
  return [...seen];
};

export const toFormValues = (wasteStream: WasteStreamProfileData): WasteProfileFormValues => {
  const profile = wasteStream.profile;
  return {
    physicalState: profile?.physicalState ?? null,
    phLow: profile?.phLow ?? null,
    phHigh: profile?.phHigh ?? null,
    flashPointF: profile?.flashPointF ?? null,
    flashPointMinF: profile?.flashPointMinF ?? null,
    flashPointMaxF: profile?.flashPointMaxF ?? null,
    flashPointQualifier: profile?.flashPointQualifier ?? null,
    specificGravity: profile?.specificGravity ?? null,
    physicalDescription: profile?.physicalDescription ?? "",
    isIgnitable: profile?.isIgnitable ?? false,
    isCorrosive: profile?.isCorrosive ?? false,
    isReactive: profile?.isReactive ?? false,
    isToxic: profile?.isToxic ?? false,
    generatingProcess: profile?.generatingProcess ?? "",
    basis: profile?.basis ?? null,
    federalWasteCodes: (profile?.federalWasteCodes ?? []).join(", "),
    stateWasteCodes: (profile?.stateWasteCodes ?? []).join(", "),
    profileNumber: profile?.profileNumber ?? "",
    tsdfName: profile?.tsdfName ?? "",
    tsdfProfileId: profile?.tsdfProfileId ?? "",
    constituents: (profile?.constituents ?? []).map((constituent) => ({
      name: constituent.name,
      casNumber: constituent.casNumber ?? "",
      minPercent: constituent.minPercent ?? null,
      maxPercent: constituent.maxPercent ?? null,
    })),
    dotProperShippingName: wasteStream.dotProperShippingName ?? "",
    dotIdNumber: wasteStream.dotIdNumber ?? "",
    dotHazardClass: wasteStream.dotHazardClass ?? "",
    dotSubsidiaryHazardClass: wasteStream.dotSubsidiaryHazardClass ?? "",
    dotPackingGroup: wasteStream.dotPackingGroup ?? null,
    dotHazardous: wasteStream.dotHazardous ?? false,
  };
};

/**
 * Every row as typed, including one left wholly blank.
 *
 * Blank rows are dropped at the write rather than here, because the compliance
 * engine keys a constituent issue by its position in this list — and the form
 * renders issues against the position a row sits at on screen. Filtering first
 * makes those two disagree: with an empty row above it, a row the engine reports
 * as `constituents.1` is the third row the reviewer can see, so the error
 * appears on somebody else's chemical.
 */
const toConstituentInputs = (rows: ConstituentFormRow[]): WasteProfileConstituentInput[] =>
  rows.map((row) => ({
    name: row.name?.trim() ?? "",
    casNumber: toNullableString(row.casNumber),
    minPercent: toFlexibleNumber(row.minPercent),
    maxPercent: toFlexibleNumber(row.maxPercent),
  }));

export const toProfileInput = (
  wasteStreamId: string,
  values: WasteProfileFormValues
): WasteProfileInput => ({
  wasteStreamId,
  physicalState: values.physicalState,
  phLow: toFlexibleNumber(values.phLow),
  phHigh: toFlexibleNumber(values.phHigh),
  flashPointF: toFlexibleNumber(values.flashPointF),
  flashPointMinF: toFlexibleNumber(values.flashPointMinF),
  flashPointMaxF: toFlexibleNumber(values.flashPointMaxF),
  flashPointQualifier: values.flashPointQualifier,
  specificGravity: toFlexibleNumber(values.specificGravity),
  physicalDescription: toNullableString(values.physicalDescription),
  isIgnitable: values.isIgnitable,
  isCorrosive: values.isCorrosive,
  isReactive: values.isReactive,
  isToxic: values.isToxic,
  generatingProcess: toNullableString(values.generatingProcess),
  basis: values.basis,
  federalWasteCodes: splitCodeList(values.federalWasteCodes),
  stateWasteCodes: splitCodeList(values.stateWasteCodes),
  profileNumber: toNullableString(values.profileNumber),
  tsdfName: toNullableString(values.tsdfName),
  tsdfProfileId: toNullableString(values.tsdfProfileId),
  constituents: toConstituentInputs(values.constituents),
});

export const toDotInput = (
  wasteStreamId: string,
  values: WasteProfileFormValues
): WasteStreamDotProfileInput => ({
  wasteStreamId,
  dotProperShippingName: toNullableString(values.dotProperShippingName),
  dotIdNumber: toNullableString(values.dotIdNumber),
  dotHazardClass: toNullableString(values.dotHazardClass),
  dotSubsidiaryHazardClass: toNullableString(values.dotSubsidiaryHazardClass),
  dotPackingGroup: values.dotPackingGroup,
  dotHazardous: values.dotHazardous,
});

const HAZARD_CLASS_FIELDS = ["dotHazardClass", "dotSubsidiaryHazardClass"] as const;

/** The same helper the server runs, picked by field so the wording names
 * the subsidiary when that is the field at fault. */
const problemForHazardClassField = (
  key: (typeof HAZARD_CLASS_FIELDS)[number],
  value: string
): string | undefined =>
  key === "dotHazardClass" ? hazardClassProblem(value) : subsidiaryHazardClassProblem(value);

/**
 * The hazard class rules (a division where the class has them, a real DOT
 * class otherwise). The engine validator only sees the characterization, so
 * the form checks them here, with the same helpers the server uses. Keyed by
 * form field so each issue lands on its input.
 */
export const hazardClassIssues = (values: WasteProfileFormValues): Issue[] => {
  const fieldIssues: Issue[] = HAZARD_CLASS_FIELDS.flatMap((key) => {
    const message = problemForHazardClassField(key, values[key]);
    return message ? [{ key, modelName: "WasteStream", level: IssueLevel.Error, message }] : [];
  });
  // The pair is judged only once each value is valid on its own.
  const pair =
    fieldIssues.length === 0
      ? hazardClassPairProblem(values.dotHazardClass, values.dotSubsidiaryHazardClass)
      : undefined;
  return pair
    ? [
        ...fieldIssues,
        {
          key: "dotSubsidiaryHazardClass",
          modelName: "WasteStream",
          level: IssueLevel.Error,
          message: pair,
        },
      ]
    : fieldIssues;
};
